import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ThumbsUp, ThumbsDown, Flag, AlertTriangle, CheckCircle,
  Mail, MapPin, ChevronDown, ChevronUp
} from "lucide-react";

const COMPANY = "Kosansh Solutions Inc";
const DBA = "ActivitySports.com";
const EMAIL = "techieroshan@gmail.com";

const values = [
  {
    title: "Sport for everyone",
    body: "The ActivitySports community is for people of all skill levels, backgrounds, genders, ages, and abilities. We don't tolerate gatekeeping, elitism, or exclusion.",
  },
  {
    title: "Good faith, every time",
    body: "Represent yourself honestly — your skill, your availability, your intent. Partner matching only works when everyone plays fair.",
  },
  {
    title: "Respect on and off the court",
    body: "Treat every match with the respect you'd want yourself. Ghosting, trash talk, and condescension have no place here.",
  },
  {
    title: "Safety first",
    body: "Never use this platform to compromise someone's safety, gather personal data, or facilitate harm. Read our Safety Center for full guidance.",
  },
];

const rules = [
  {
    category: "Prohibited — Account Integrity",
    color: "red",
    items: [
      "Creating multiple accounts to circumvent bans, boosted profile limits, or other restrictions",
      "Misrepresenting your identity, gender, location, age, or skill rating",
      "Using bots, scripts, or automated tools to access the Platform",
      "Sharing your account with another person",
    ],
  },
  {
    category: "Prohibited — Content Standards",
    color: "red",
    items: [
      "Uploading sexually explicit, nude, or violent images or content",
      "Posting content that glorifies or promotes self-harm, eating disorders, or substance abuse",
      "Defaming, impersonating, or publishing private information about another person ('doxing')",
      "Spreading verifiably false information that could deceive or harm users",
      "Content that promotes, recruits for, or funds hate groups or extremist organizations",
    ],
  },
  {
    category: "Prohibited — Interaction Standards",
    color: "red",
    items: [
      "Harassing, threatening, or intimidating any user — including after they block you",
      "Sending unsolicited romantic or sexual messages to other users",
      "Sending commercial solicitations, spam, or multi-level marketing messages",
      "Using the messaging system to collect personal data for external use",
      "Repeated no-shows, ghost cancellations, or bad-faith session requests",
    ],
  },
  {
    category: "Prohibited — Discrimination",
    color: "red",
    items: [
      "Rejecting, insulting, or excluding users based on race, ethnicity, national origin, religion, gender identity, sexual orientation, disability, or age",
      "Posting biased reviews, ratings, or comments intended to demean protected groups",
      "Using coded or dog-whistle language to discriminate while appearing neutral",
    ],
  },
  {
    category: "Required — Honest Sport",
    color: "green",
    items: [
      "Self-report your skill rating honestly using recognized scales (NTRP, DUPR, handicap, V-Scale/YDS)",
      "Cancel sessions with as much notice as possible — at minimum 2 hours in advance",
      "If you believe a match significantly misrepresented their skill, report it rather than ghosting",
      "Only participate in corporate wellness leagues as a representative of the stated company",
    ],
  },
];

const enforcement = [
  {
    level: "Warning",
    trigger: "First-time minor violations (e.g., a single lapse in tone)",
    outcome: "Written notice via email; content removed if applicable",
    duration: "No account restriction",
  },
  {
    level: "Temporary Suspension",
    trigger: "Repeated minor violations or a single serious violation",
    outcome: "Account suspended; all active sessions cancelled; pending messages blocked",
    duration: "7 to 30 days",
  },
  {
    level: "Permanent Ban",
    trigger: "Severe violations, repeated serious violations, or criminal conduct",
    outcome: "Account permanently terminated; data retained for legal compliance; device fingerprint banned",
    duration: "Permanent",
  },
  {
    level: "Immediate Emergency Action",
    trigger: "Active threat to user safety, CSAM, or credible violence",
    outcome: "Immediate account termination, referral to law enforcement",
    duration: "Permanent; may involve law enforcement",
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-brand-navy text-sm">{q}</span>
        {open ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-gray-600 border-t border-gray-100 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function CommunityGuidelinesPage() {
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero */}
      <div className="bg-brand-navy text-white py-14 px-4 dark-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-widest mb-3">
            <Users size={14} /> Community
          </div>
          <h1 className="text-4xl md:text-5xl font-hero font-black mb-3">Community Guidelines</h1>
          <p className="text-white/70 max-w-2xl">
            {DBA} is built on mutual respect between players. These guidelines define what it means to
            be a good community member — and what happens when these standards are not met.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-14 space-y-16">

        {/* Our Values */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Our Core Values</h2>
          <p className="text-gray-600 mb-8">Everything we enforce stems from these four beliefs:</p>
          <div className="grid md:grid-cols-2 gap-5">
            {values.map(({ title, body }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ThumbsUp className="text-brand-green" size={16} />
                  <h3 className="font-bold text-brand-navy text-sm">{title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Rules */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">The Rules</h2>
          <p className="text-gray-600 mb-8">
            Violating these rules will result in enforcement action (see below). Serious violations
            may result in immediate permanent termination without warning.
          </p>
          <div className="space-y-6">
            {rules.map(({ category, color, items }) => (
              <div key={category} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className={`px-5 py-3 flex items-center gap-2 ${color === "red" ? "bg-red-50 border-b border-red-100" : "bg-green-50 border-b border-green-100"}`}>
                  {color === "red" ? (
                    <ThumbsDown size={14} className="text-red-500" />
                  ) : (
                    <CheckCircle size={14} className="text-brand-green" />
                  )}
                  <h3 className={`font-bold text-sm ${color === "red" ? "text-red-700" : "text-green-700"}`}>{category}</h3>
                </div>
                <ul className="divide-y divide-gray-50">
                  {items.map((item) => (
                    <li key={item} className="px-5 py-3 flex items-start gap-3 text-sm text-gray-700">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${color === "red" ? "bg-red-400" : "bg-brand-green"}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Enforcement */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Enforcement</h2>
          <p className="text-gray-600 mb-6">
            Our Trust & Safety team reviews all reports and applies the following framework consistently.
            We reserve the right to escalate immediately based on severity.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-navy text-white dark-section">
                  <th className="text-left px-4 py-3 font-semibold rounded-tl-xl">Level</th>
                  <th className="text-left px-4 py-3 font-semibold">Triggered By</th>
                  <th className="text-left px-4 py-3 font-semibold">Outcome</th>
                  <th className="text-left px-4 py-3 font-semibold rounded-tr-xl">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {enforcement.map(({ level, trigger, outcome, duration }) => (
                  <tr key={level} className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-brand-navy">{level}</td>
                    <td className="px-4 py-3 text-gray-600">{trigger}</td>
                    <td className="px-4 py-3 text-gray-600">{outcome}</td>
                    <td className="px-4 py-3 text-gray-500">{duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Reporting */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">How to Report</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Flag className="text-brand-orange" size={18} />
                <h3 className="font-bold text-brand-navy text-sm">In-App Reporting</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Click the <strong>⋯ menu</strong> or the flag icon on any user profile,
                message thread, or club post. Select a category and optionally add context.
                Reports are anonymous to the reported user.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="text-brand-green" size={18} />
                <h3 className="font-bold text-brand-navy text-sm">Email Report</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                For complex reports or situations that require more context, email our Trust & Safety
                team at{" "}
                <a href={`mailto:${EMAIL}?subject=Community Report`} className="text-brand-green underline underline hover:underline">{EMAIL}</a>.
                Include the username and a description of the incident.
              </p>
            </div>
          </div>
        </section>

        {/* Appeals */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Appeals Process</h2>
          <p className="text-gray-600 mb-6">
            We believe in proportionate and fair enforcement. If you believe an action taken against
            your account was incorrect, you may appeal:
          </p>
          <ol className="space-y-3">
            {[
              "Within 14 days of the enforcement action, email us at " + EMAIL + " with subject \"Account Appeal\"",
              "Include your account email, the action taken, and your reason for appeal",
              "A senior member of the Trust & Safety team (different from the original reviewer) will review your appeal",
              "We will respond within 10 business days with a final decision",
              "Permanent bans for severe violations (including violence, CSAM, or credible threats) are not eligible for appeal",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 bg-brand-navy text-white rounded-full text-xs flex items-center justify-center font-bold dark-section">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {[
              {
                q: "Can I see who reported me?",
                a: "No. Reports are anonymous to protect the safety of reporters. This is essential to ensuring users feel safe coming forward about serious issues.",
              },
              {
                q: "What happens to my pending sessions if I'm suspended?",
                a: "Active session invitations and confirmed sessions will be cancelled automatically. We will notify all affected users that the session could not proceed.",
              },
              {
                q: "I was falsely reported. What do I do?",
                a: "If no violation is found after our review, no action is taken. If action was taken, use the Appeals Process above. We take false reporting seriously and may act against users who repeatedly file bad-faith reports.",
              },
              {
                q: "Does a warning stay on my record?",
                a: "Warnings remain in your account history for 12 months and are factored into future enforcement decisions. After 12 months they are archived but not deleted.",
              },
              {
                q: "Can I use ActivitySports to find a coach or paid instructor?",
                a: "The Platform is designed for peer partner matching, not commercial coaching. If you are a coach seeking to advertise paid lessons, contact us for our Club Pro program instead.",
              },
              {
                q: "What's your policy on skill-rating inflation?",
                a: "Deliberately misrepresenting your skill level to attract or frustrate matches is a violation. We may adjust ratings based on community feedback and reserve the right to lock ratings on egregious cases.",
              },
            ].map(({ q, a }) => (
              <FaqItem key={q} q={q} a={a} />
            ))}
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-brand-navy mb-4">Contact Our Community Team</h2>
          <div className="bg-brand-navy/5 border border-brand-navy/20 rounded-2xl p-6 space-y-3">
            <p className="font-bold text-brand-navy">{COMPANY} — Community & Trust Team</p>
            <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={13} />Fort Worth, TX, United States</div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail size={13} />
              <a href={`mailto:${EMAIL}?subject=Community Inquiry`} className="text-brand-green underline underline hover:underline">{EMAIL}</a>
            </div>
            <p className="text-xs text-gray-400 mt-2">Response time: 2–3 business days for standard inquiries, 24 hours for high-priority safety reports.</p>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <Link to="/privacy" className="text-brand-green underline underline hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-brand-green underline underline hover:underline">Terms of Service</Link>
            <Link to="/safety" className="text-brand-green underline underline hover:underline">Safety Center</Link>
          </div>
        </section>

      </div>
    </div>
  );
}
